import React, { useCallback, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  isValidString,
  LOCAL_STORAGE_KEYS,
  RetrieveFromLocalCache,
  setLocalStorageValue,
} from "../common/CommonUtilsMethods";
import "./CookieConsentBanner.css";
import { Cookie } from 'lucide-react';


const COOKIE_CONSENT_ACCEPTED: string = "accepted";
const COOKIE_CONSENT_REJECTED: string = "rejected";

enum CookieConsentButtonVariant {
  PRIMARY = "primary",
  SECONDARY = "secondary",
}

type WindowWithGtag = Window & {
  readonly gtag?: (...args: readonly unknown[]) => void;
};

interface CookieConsentRootProps {
  readonly children: React.ReactNode;
}

interface CookieConsentActionsProps {
  readonly onAccept: () => void;
  readonly onReject: () => void;
}

interface CookieConsentButtonProps {
  readonly children: React.ReactNode;
  readonly onClick: () => void;
  readonly variant: CookieConsentButtonVariant;
}

const hasStoredConsent = (): boolean => {
  const stored: string | null = RetrieveFromLocalCache(LOCAL_STORAGE_KEYS.COOKIE_CONSENT_STATUS);
  return isValidString(stored);
};

const pushGoogleConsentGranted = (): void => {
  try {
    const w: WindowWithGtag = window as WindowWithGtag;
    if (typeof w.gtag === "function") {
      w.gtag("consent", "update", {
        ad_storage: "granted",
        ad_user_data: "granted",
        ad_personalization: "granted",
        analytics_storage: "granted",
        personalization_storage: "granted",
      });
    }
  } catch (error) {
    console.error("CookieConsentBanner: failed to push consent update", error);
  }
};

const CookieConsentRoot = ({ children }: CookieConsentRootProps): React.ReactElement => (
  <section className="cookie-consent-banner" role="dialog" aria-label="Cookie consent">
    <div className="cookie-consent-banner__content">{children}</div>
  </section>
);

const CookieConsentIcon = (): React.ReactElement => (
  <span className="cookie-consent-banner__icon" aria-hidden="true">
    <Cookie size={28} />
  </span>
);

const CookieConsentPolicyLink = (): React.ReactElement => (
  <RouterLink className="cookie-consent-banner__link" to="/privacy">
    Privacy Policy
  </RouterLink>
);

const CookieConsentMessage = (): React.ReactElement => (
  <p className="cookie-consent-banner__message">
    We use cookies and similar technologies for site functionality, analytics, and marketing. See our{" "}
    <CookieConsentPolicyLink />.
  </p>
);

const CookieConsentButton = ({
  children,
  onClick,
  variant,
}: CookieConsentButtonProps): React.ReactElement => (
  <button
    className={`cookie-consent-banner__button cookie-consent-banner__button--${variant}`}
    type="button"
    onClick={onClick}
  >
    {children}
  </button>
);

const CookieConsentActions = ({ onAccept, onReject }: CookieConsentActionsProps): React.ReactElement => (
  <div className="cookie-consent-banner__actions">
    <CookieConsentButton onClick={onReject} variant={CookieConsentButtonVariant.SECONDARY}>
      Reject non-essential
    </CookieConsentButton>
    <CookieConsentButton onClick={onAccept} variant={CookieConsentButtonVariant.PRIMARY}>
      Accept all
    </CookieConsentButton>
  </div>
);

const CookieConsentBanner = (): React.ReactElement | null => {
  const [dismissed, setDismissed] = useState<boolean>(hasStoredConsent);

  const onAccept = useCallback((): void => {
    setLocalStorageValue({
      key: LOCAL_STORAGE_KEYS.COOKIE_CONSENT_STATUS,
      value: COOKIE_CONSENT_ACCEPTED,
    });
    pushGoogleConsentGranted();
    setDismissed(true);
  }, []);

  const onReject = useCallback((): void => {
    setLocalStorageValue({
      key: LOCAL_STORAGE_KEYS.COOKIE_CONSENT_STATUS,
      value: COOKIE_CONSENT_REJECTED,
    });
    setDismissed(true);
  }, []);

  if (dismissed) {
    return null;
  }

  return (
    <CookieConsentRoot>
      <div className="cookie-consent-banner__message-group">
        <CookieConsentIcon />
        <CookieConsentMessage />
      </div>
      <CookieConsentActions onAccept={onAccept} onReject={onReject} />
    </CookieConsentRoot>
  );
};

export { CookieConsentBanner };
